// file: DriveYear.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getUserInfoByUserId } from "../../../../../services/userServices/UserInfoService";
import {
  Participant,
  ParticipantYear,
  DriveItem,
} from "../../../../../interfaces/caseFileDrive/DriveFiles";
import { getDriveData } from "../../../../../services/fileServices/FileService";
import { useAuth } from "../../../../../firebase/AuthProvider";

// Import the file-to-driveItems mapper
import DriveList from "../driveItems/DriveList";
import { mapFilesToDriveItems } from "../../../../../utilities/MapFunctions";
import { faGripVertical, faList } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDriveContext } from "../../../../../contexts/DriveContext";
import DriveSearch from "../driveItems/DriveSearch";
import { Form, Table } from "react-bootstrap";
import searchicon from "../../../../../assets/images/searchicon.svg";
import foldericon from "../../../../../assets/images/foldericon.svg";
import girdviewicon from "../../../../../assets/images/girdviewicon.svg";
import listviewicon from "../../../../../assets/images/listviewicon.svg";
import smallclockicon from "../../../../../assets/images/smallclockicon.svg";
import folderlgicon from "../../../../../assets/images/folderlgicon.svg";
import pdficon from "../../../../../assets/images/pdficon.svg";
import backarrowbtn from "../../../../../assets/images/backarrowbtn.svg";
import { formatFileSize } from "../../../../../utilities/fileUtils";
import { convertUtcToLocalDate } from "../../../../../utilities/dateUtils";
import "./CaseFileStyles.css";
import { FileText, FolderOpen, Grid3x3, List } from "lucide-react";
import AvatarImage from "../../../../ui/Avatar/AvatarImage";

const DriveYear: React.FC = () => {
  const { currentUser, currentRoles } = useAuth();
  const { participantId, year } = useParams();
  const navigate = useNavigate();

  const [participant, setParticipant] = useState<Participant | null>(null);
  const [yearData, setYearData] = useState<ParticipantYear | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>(""); // for filtering
  const [items, setItems] = useState<DriveItem[]>([]); // For DriveList
  const { viewMode, setViewMode } = useDriveContext();
  const [loading, setLoading] = useState<boolean>(true);
  const hasHigherRoleThanSC = currentRoles?.some((role) => role.name !== "SC");
  const { employeeId } = useParams<{ employeeId?: string }>();
  const [itemStructure, setItemStucture] = useState<"grid" | "list">("list");
  const [employeeName, setEmployeeName] = useState<string>("");

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        if (currentUser) {
          const authToken = await currentUser.getIdToken();
          let data;
          if (employeeId && hasHigherRoleThanSC) {
            const userInfo = await getUserInfoByUserId(employeeId, authToken);
            if (userInfo) {
              setEmployeeName(`${userInfo.firstName} ${userInfo.lastName}`);
            }
            data = await getDriveData(authToken, employeeId);
          } else data = await getDriveData(authToken); // data is an array of Participant
          const foundParticipant = data.find(
            (p) => p.participantId.toString() === participantId
          );
          if (foundParticipant) {
            const foundYear = foundParticipant.years.find(
              (y) => y.year.toString() === year
            );
            setParticipant(foundParticipant);
            setYearData(foundYear ?? null);

            if (foundYear) {
              // Convert the year’s files to DriveItem[]
              const fileItems = mapFilesToDriveItems(foundYear.files);
              setItems(fileItems);
            } else {
              setItems([]);
            }
          } else {
            setParticipant(null);
            setYearData(null);
            setItems([]);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [participantId, year, currentUser, employeeId, hasHigherRoleThanSC]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!participant || !yearData) {
    return (
      <div>
        <p>Year data not found.</p>
        <button
          className="border rounded-lg p-1 shadow-sm hover:shadow-none mb-2 bg-white"
          onClick={() =>
            navigate(
              `/drive/caseFileDrive/${
                employeeId ? "employee/" + employeeId + "/" : ""
              }participant/${participantId}`
            )
          }
        >
          {"<"} Back
        </button>
      </div>
    );
  }

  // Filter items by searchTerm (case-insensitive by name)
  const filteredItems = items.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Called when user clicks a file
  const handleItemClick = (item: DriveItem) => {
    if (item.isFolder) {
    } else {
      // It's a file
      navigate(`/view/file/${item.id}`);
      // Possibly open a preview or navigate somewhere else
    }
  };

  return (
    <>
      <div className="common_content_inner mby_t_24">
        <div className="top top_bar_flx">
          <div className="participant-search">
            <div className="custom_input_flled filled_input_icon mb-0">
              <input
                type="search"
                placeholder="Search participant"
                className="sm_input"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <span className="icon_img cursor-pointer">
                <img src={searchicon} alt="search" />
              </span>
            </div>
          </div>
          <div className="top top_bar_flx participant">
            <div className="top_right_flx participant participant_files_top_right">
              <span
                className={`viewicon ${
                  itemStructure === "grid" ? "active-view" : ""
                }`}
                onClick={() => setItemStucture("grid")}
              >
                <Grid3x3 className="icon-svg" />
              </span>
              <span
                className={`viewicon ${
                  itemStructure === "list" ? "active-view" : ""
                }`}
                onClick={() => setItemStucture("list")}
              >
                <List className="icon-svg" />
              </span>
            </div>
          </div>
        </div>
        <div className="mb-2 flex gap-2 justify-between flex-wrap">
          <div className="flex items-center gap-2">
            {employeeId && hasHigherRoleThanSC ? (
              <>
                <div
                  className="text-[#776ce6] cursor-pointer"
                  onClick={() => navigate("/drive/caseFileDrive")}
                >
                  My Team
                </div>
                <span> / </span>
                <div
                  className="text-[#776ce6] cursor-pointer"
                  onClick={() =>
                    navigate(`/drive/caseFileDrive/employee/${employeeId}`)
                  }
                >
                  {employeeName || employeeId}
                </div>
                <span> / </span>
                <span>{participant?.participantName}</span>
              </>
            ) : (
              <>
                <div
                  className="text-[#776ce6] cursor-pointer"
                  onClick={() => navigate("/drive/caseFileDrive")}
                >
                  Participant Files
                </div>
                <span> / </span>
                <span>{participant?.participantName}</span>
              </>
            )}
          </div>
          <div className="top_bar_tittle">
            <h2>
              {/* <button
                onClick={() =>
                  navigate(
                    `/drive/caseFileDrive/${
                      employeeId ? "employee/" + employeeId : ""
                    }`
                  )
                }
              >
                <img src={backarrowbtn} alt="back arrow" className="icon_sm" />
              </button> */}
              {/* Participant Files */}
              <div
                className="profile_link"
                onClick={() =>
                  navigate(`/participant/${participantId}/participant-snapshot`)
                }
              >
                View Profile
              </div>
            </h2>
          </div>
        </div>

        {itemStructure === "grid" && (
          <div className="participantfiles_grid">
            {filteredItems.map((item, index) => (
              <div
                className="participantfiles_card cursor-pointer"
                key={index}
                onClick={() => handleItemClick(item)}
              >
                <div className="participantfiles_grid_flx">
                  {/* <h3>
                          <span>
                            <img
                              src={item.isFolder ? foldericon : pdficon}
                              alt="foldericon"
                            />
                          </span>

                          {item.name}
                        </h3> */}
                  {item.isFolder ? (
                    <span className="tableicon-card">
                      {" "}
                      <FolderOpen className="tableicon-svg" />
                    </span>
                  ) : (
                    <span className="tableicon-card">
                      <FileText className="tableicon-svg" />
                    </span>
                  )}
                  <div className="grid-file-name">{item.name}</div>
                  <div className="grid-updated">
                    Updated:{" "}
                    {item?.updateDate
                      ? (() => {
                          const localDate = convertUtcToLocalDate(item.updateDate);
                          return localDate ? localDate.toLocaleDateString() : "N/A";
                        })()
                      : "N/A"}
                  </div>
                </div>
                <div className="participant-grid-bottom">
                  <div className="avatar-container">
                    <AvatarImage userId={item.name} />
                  </div>
                  <div className="participant-name">{item.name}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {itemStructure === "list" && (
          <Table responsive className="drive-table">
            <thead>
              <tr>
                <th className="th-head">
                  Name <span className="text-[#6A7282]">⇅</span>
                </th>

                <th className="th-head">
                  Participant <span className="text-[#6A7282]">⇅</span>
                </th>

                <th className="th-head">
                  Created <span className="text-[#6A7282]">⇅</span>
                </th>

                <th className="th-head">
                  Last Updated <span className="text-[#6A7282]">⇅</span>
                </th>

                <th className="th-head">
                  File Size <span className="text-[#6A7282]">⇅</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item, index) => (
                <tr key={index} onClick={() => handleItemClick(item)}>
                  {/* Folder icon column */}
                  <td>
                    <div className="tableicon-tab">
                      {item.isFolder ? (
                        <span className="tableicon">
                          {" "}
                          <FolderOpen className="tableicon-svg" />
                        </span>
                      ) : (
                        <span className="tableicon">
                          <FileText className="tableicon-svg" />
                        </span>
                      )}
                      <span className="font-normal text-[#354152] text-lg tracking-[0] leading-6 truncate">
                        {item.name}
                      </span>
                    </div>
                  </td>

                  {/* Name */}
                  <td>{item.name}</td>

                  {/* Created date */}
                  <td>
                    {item?.updateDate
                      ? (() => {
                          const localDate = convertUtcToLocalDate(item.updateDate);
                          return localDate ? localDate.toLocaleDateString() : "N/A";
                        })()
                      : "N/A"}
                  </td>

                  {/* Last Updated */}
                  <td>
                    {item?.updateDate
                      ? (() => {
                          const localDate = convertUtcToLocalDate(item.updateDate);
                          return localDate ? localDate.toLocaleDateString() : "N/A";
                        })()
                      : "N/A"}
                  </td>

                  {/* File size */}
                  <td>{formatFileSize(item.size ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
      {/* <div>
      <div className="flex justify-between items-start align-middle">
        <button
          className="border rounded-lg p-1 shadow-sm hover:shadow-none mb-2 text-xs bg-white"
          onClick={() => navigate(`/drive/caseFileDrive/${employeeId ? "employee/" + employeeId + "/" : "" }participant/${participantId}`)}
        >
          {"<"} Back
        </button>
        <div className="flex flex-col">
          <div className="flex justify-end">
            <DriveSearch
              onSearch={(query) => setSearchTerm(query)}
              placeholder="Search files..."
            />
          </div>
          <div className="flex gap-2 mb-2 justify-end">
            Display:
            <button
              className={
                viewMode === "grid"
                  ? "bg-[#3DD598] text-white w-7 h-7"
                  : "bg-gray-200 w-7 h-7"
              }
              onClick={() => setViewMode("grid")}
            >
              <FontAwesomeIcon icon={faGripVertical} />
            </button>
            <button
              className={
                viewMode === "table"
                  ? "bg-[#3DD598] text-white w-7 h-7"
                  : "bg-gray-200 w-7 h-7"
              }
              onClick={() => setViewMode("table")}
            >
              <FontAwesomeIcon icon={faList} />
            </button>
          </div>
        </div>
      </div>
      <h2 className="text-text inter-font font-bold text-xl mb-3">
        {participant.participantName} / {yearData.year}
      </h2>
      <DriveList
        items={filteredItems}
        viewMode={viewMode}
        onItemClick={handleItemClick}
        onRightClickItem={() => {}}
      />
    </div> */}
    </>
  );
};

export default DriveYear;
